import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { trackEngagement } from '../utils/analytics';
import {
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const HelpCenter = () => {
  // Track help center view
  useEffect(() => {
    trackEngagement.viewHelpCenter();
  }, []);
  
  const faqs = [
    {
      question: "How do I connect my Instagram account?",
      answer: "Navigate to your Dashboard and click on the 'Connect' button in the Instagram section. You'll be redirected to Instagram to authorize Vaulto to access your content."
    },
    {
      question: "How often does Vaulto backup my content?",
      answer: "Vaulto performs automatic backups daily for Premium users and weekly for Free users. You can also trigger manual backups anytime from your dashboard."
    },
    {
      question: "Where is my content stored?",
      answer: "Your content is securely backed up to your connected Google Drive account. Vaulto creates a dedicated folder structure to organize your Instagram content."
    },
    {
      question: "Can I download my backed-up content?",
      answer: "Yes! You can access all your backed-up content directly from Google Drive. Premium users can also use our bulk download feature from the Backups page."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use industry-standard encryption for all data transfers. We never store your Instagram content on our servers - it goes directly to your Google Drive."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "You can cancel your subscription anytime from the Settings page. Click on 'Subscription' and then 'Manage Subscription' to access your billing portal."
    }
  ];

  const contactMethods = [
    {
      icon: <ChatBubbleLeftRightIcon className="h-6 w-6" />,
      title: "Live Chat",
      description: "Chat with our support team",
      availability: "Mon-Fri, 9am-5pm PST",
      action: "Start Chat"
    },
    {
      icon: <EnvelopeIcon className="h-6 w-6" />,
      title: "Email Support",
      description: "Get help via email",
      contact: "admin@vaulto.social",
      action: "Send Email"
    },
    {
      icon: <DocumentTextIcon className="h-6 w-6" />,
      title: "Documentation",
      description: "Browse our knowledge base",
      link: "/docs",
      action: "View Docs"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <Navbar />
      
      <main className="flex-grow pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              How can we help you?
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Find answers to common questions or get in touch with our support team
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help..."
                className="w-full px-6 py-4 bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
              <QuestionMarkCircleIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            </div>
          </div>

          {/* Contact Methods */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {contactMethods.map((method, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl mb-4">
                  {method.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{method.title}</h3>
                <p className="text-gray-300 mb-4">{method.description}</p>
                {method.availability && (
                  <p className="text-sm text-gray-400 mb-4">
                    <ClockIcon className="inline h-4 w-4 mr-1" />
                    {method.availability}
                  </p>
                )}
                {method.contact && (
                  <p className="text-sm text-gray-300 mb-4">{method.contact}</p>
                )}
                {method.title === "Live Chat" ? (
                  <button 
                    onClick={() => window.dispatchEvent(new Event('openVaultoChat'))}
                    className="text-pink-400 hover:text-pink-300 font-medium transition-colors"
                  >
                    {method.action} →
                  </button>
                ) : method.contact ? (
                  <a 
                    href={`mailto:${method.contact}`}
                    className="text-pink-400 hover:text-pink-300 font-medium transition-colors"
                  >
                    {method.action} →
                  </a>
                ) : method.link ? (
                  <Link 
                    to={method.link}
                    className="text-pink-400 hover:text-pink-300 font-medium transition-colors"
                  >
                    {method.action} →
                  </Link>
                ) : null}
              </div>
            ))}
          </div>

          {/* FAQs */}
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Still Need Help */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-pink-500/20 to-purple-600/20 backdrop-blur-lg rounded-3xl p-12 border border-white/20 max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-white mb-4">
                Still need help?
              </h2>
              <p className="text-gray-300 mb-8">
                Our support team is here to help you with any questions or issues you may have.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:admin@vaulto.social"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 transition-all duration-200"
                >
                  <EnvelopeIcon className="h-5 w-5 mr-2" />
                  Email Support
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 border border-white/20 text-base font-medium rounded-xl text-white hover:bg-white/10 transition-all duration-200"
                >
                  Contact Form
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer variant="dark" />
    </div>
  );
};

export default HelpCenter; 